import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Star, Send, MessageSquare, Loader2 } from "lucide-react";
import { toast } from "sonner";

const CRITERIA = [
  { key: "quality_rating", label: "Qualité du cours" },
  { key: "pedagogy_rating", label: "Pédagogie" },
  { key: "punctuality_rating", label: "Ponctualité" },
  { key: "mastery_rating", label: "Maîtrise de la matière" },
  { key: "behavior_rating", label: "Comportement" },
];

function StarRating({ value, onChange, testId }) {
  return (
    <div className="flex gap-1" data-testid={testId}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          className={`transition-transform hover:scale-110 ${n <= value ? "text-amber-500" : "text-slate-300"}`}
          data-testid={`${testId}-${n}`}
          aria-label={`Note ${n}`}
        >
          <Star className="h-7 w-7" fill={n <= value ? "currentColor" : "none"} />
        </button>
      ))}
    </div>
  );
}

export default function EvaluationsSubmitPage() {
  const [teachers, setTeachers] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({
    quality_rating: 0, pedagogy_rating: 0, punctuality_rating: 0,
    mastery_rating: 0, behavior_rating: 0, comment: "", course_subject: "",
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/users?role=teacher");
      setTeachers(Array.isArray(data) ? data : []);
    } catch {
      setTeachers([]);
    }
  };
  useEffect(() => { load(); }, []);

  const openFor = (t) => {
    setSelected(t);
    setForm({
      quality_rating: 0, pedagogy_rating: 0, punctuality_rating: 0,
      mastery_rating: 0, behavior_rating: 0, comment: "", course_subject: "",
    });
    setOpen(true);
  };

  const submit = async () => {
    if (CRITERIA.some(c => form[c.key] === 0)) {
      toast.error("Veuillez noter chaque critère (1 à 5 étoiles).");
      return;
    }
    setSaving(true);
    try {
      await api.post("/erp/evaluations", { teacher_id: selected.id, ...form });
      toast.success("Merci ! Votre évaluation anonyme a été enregistrée.");
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="eval-submit-page">
      <div>
        <div className="chip mb-2"><MessageSquare className="h-3 w-3" /><span>ÉVALUATION ANONYME</span></div>
        <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Évaluer mes enseignants</h1>
        <p className="text-slate-500 mt-1 max-w-2xl">
          Votre évaluation est <strong>100% anonyme</strong>. Aucun enseignant ne verra qui a évalué. Vos réponses sont transmises uniquement au Fondateur et à la Direction pour améliorer la qualité pédagogique.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {teachers.map((t) => (
          <div key={t.id} className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm flex flex-col" data-testid={`teacher-card-${t.id}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#1E3A8A] to-[#EA580C] text-white flex items-center justify-center font-bold text-lg">
                {(t.first_name?.[0] || "?")}{(t.last_name?.[0] || "")}
              </div>
              <div className="min-w-0">
                <div className="font-heading font-bold text-slate-900 truncate">{t.first_name} {t.last_name}</div>
                <div className="text-xs text-slate-500 truncate">{t.email}</div>
              </div>
            </div>
            <Button onClick={() => openFor(t)} className="mt-auto bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white gap-2" data-testid={`btn-eval-${t.id}`}>
              <Star className="h-4 w-4" />Évaluer
            </Button>
          </div>
        ))}
        {teachers.length === 0 && (
          <div className="col-span-full text-center text-slate-400 py-12">Aucun enseignant à évaluer pour le moment.</div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Évaluer {selected?.first_name} {selected?.last_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg text-xs text-blue-900">
              🔒 Évaluation totalement anonyme. Ni l&apos;enseignant, ni l&apos;administration ne connaîtront votre identité.
            </div>
            <div>
              <Label>Matière du cours (optionnel)</Label>
              <Input value={form.course_subject} onChange={e => setForm({ ...form, course_subject: e.target.value })} placeholder="Ex : Mathématiques" data-testid="eval-subject" />
            </div>
            {CRITERIA.map((c) => (
              <div key={c.key} className="flex items-center justify-between gap-3">
                <Label>{c.label}</Label>
                <StarRating value={form[c.key]} onChange={(v) => setForm({ ...form, [c.key]: v })} testId={`stars-${c.key}`} />
              </div>
            ))}
            <div>
              <Label>Commentaire (facultatif — reste anonyme)</Label>
              <Textarea value={form.comment} onChange={e => setForm({ ...form, comment: e.target.value })} rows={3} maxLength={1000} placeholder="Vos suggestions constructives..." data-testid="eval-comment" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={submit} disabled={saving} className="bg-[#166534] hover:bg-[#14532d] text-white gap-2" data-testid="eval-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4" />Envoyer</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
